import { useQuery } from '@tanstack/react-query';
import {
  Users,
  DollarSign,
  Activity,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Clock,
  RefreshCw,
} from 'lucide-react';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { api } from '../../api/client';

interface AdminStats {
  totalUsers: number;
  mrr: number;
  activeSubscriptions: number;
  healthySvcCount: number;
  totalSvcCount: number;
}

interface RecentUser {
  uid: string;
  email: string;
  plan: string;
  created_at: string;
}

interface ServiceHealth {
  name: string;
  status: 'healthy' | 'warning' | 'unhealthy' | 'stopped';
  uptime: string;
  cpuPct: number;
  memPct: number;
}

interface AdminError {
  id: string;
  service: string;
  message: string;
  occurred_at: string;
  level: 'error' | 'warn' | 'critical';
}

async function fetchStats(): Promise<AdminStats> {
  const { data } = await api.get<AdminStats>('/admin/stats');
  return data;
}

async function fetchRecentUsers(): Promise<RecentUser[]> {
  const { data } = await api.get<RecentUser[]>('/admin/recent-users');
  return data;
}

async function fetchServiceHealth(): Promise<ServiceHealth[]> {
  const { data } = await api.get<ServiceHealth[]>('/admin/service-health');
  return data;
}

async function fetchErrors(): Promise<AdminError[]> {
  const { data } = await api.get<AdminError[]>('/admin/errors');
  return data;
}

function StatCard({
  icon: Icon,
  label,
  value,
  color = 'blue',
}: {
  icon: typeof Users;
  label: string;
  value: string | number;
  color?: 'blue' | 'green' | 'purple' | 'orange';
}): JSX.Element {
  const colorMap = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-emerald-50 text-emerald-600',
    purple: 'bg-purple-50 text-purple-600',
    orange: 'bg-orange-50 text-orange-600',
  };
  return (
    <div className="card flex items-center gap-4">
      <div className={clsx('p-3 rounded-xl', colorMap[color])}>
        <Icon size={22} />
      </div>
      <div>
        <div className="text-2xl font-bold text-brand-ink">{value}</div>
        <div className="text-sm text-brand-muted">{label}</div>
      </div>
    </div>
  );
}

function StatusDot({ status }: { status: ServiceHealth['status'] }): JSX.Element {
  const map = {
    healthy: 'bg-emerald-500',
    warning: 'bg-yellow-400',
    unhealthy: 'bg-red-500',
    stopped: 'bg-slate-400',
  };
  return <span className={clsx('inline-block w-2.5 h-2.5 rounded-full', map[status])} />;
}

function StatusIcon({ status }: { status: ServiceHealth['status'] }): JSX.Element {
  if (status === 'healthy') return <CheckCircle2 size={16} className="text-emerald-500" />;
  if (status === 'warning') return <Clock size={16} className="text-yellow-500" />;
  return <XCircle size={16} className="text-red-500" />;
}

const SERVICE_NAMES = [
  'api-gateway',
  'scheduler',
  'telegram-connector',
  'postgres',
  'redis',
  'rabbitmq',
  'analytics-svc',
  'content-svc',
  'farm-svc',
];

export function AdminDashboard(): JSX.Element {
  const statsQ = useQuery({ queryKey: ['admin', 'stats'], queryFn: fetchStats, refetchInterval: 30_000 });
  const usersQ = useQuery({ queryKey: ['admin', 'recent-users'], queryFn: fetchRecentUsers });
  const healthQ = useQuery({ queryKey: ['admin', 'service-health'], queryFn: fetchServiceHealth, refetchInterval: 30_000 });
  const errorsQ = useQuery({ queryKey: ['admin', 'errors'], queryFn: fetchErrors, refetchInterval: 60_000 });

  const s = statsQ.data;
  const users = usersQ.data ?? [];
  const services = healthQ.data ?? [];
  const errors = errorsQ.data ?? [];

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-bold text-brand-ink">Admin Dashboard</h1>

      {/* Stats row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statsQ.isLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="card h-24 animate-pulse bg-slate-100" />
          ))
        ) : (
          <>
            <StatCard icon={Users} label="Total Users" value={s?.totalUsers ?? 0} color="blue" />
            <StatCard
              icon={DollarSign}
              label="MRR"
              value={`$${(s?.mrr ?? 0).toLocaleString()}`}
              color="green"
            />
            <StatCard
              icon={Activity}
              label="Active Subscriptions"
              value={s?.activeSubscriptions ?? 0}
              color="purple"
            />
            <StatCard
              icon={CheckCircle2}
              label="Healthy Services"
              value={`${s?.healthySvcCount ?? 0} / ${s?.totalSvcCount ?? SERVICE_NAMES.length}`}
              color="orange"
            />
          </>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Recent signups */}
        <div className="card">
          <h2 className="text-lg font-semibold text-brand-ink mb-4">Recent Signups</h2>
          {usersQ.isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-10 bg-slate-100 rounded animate-pulse" />
              ))}
            </div>
          ) : users.length === 0 ? (
            <p className="text-brand-muted text-sm">No recent signups.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-brand-muted border-b border-slate-100">
                    <th className="pb-2 font-medium">Email</th>
                    <th className="pb-2 font-medium">Plan</th>
                    <th className="pb-2 font-medium">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {users.slice(0, 10).map((u) => (
                    <tr key={u.uid} className="border-b border-slate-50 last:border-0">
                      <td className="py-2 text-brand-ink">{u.email}</td>
                      <td className="py-2">
                        <span className="px-2 py-0.5 rounded-full text-xs bg-brand-primary/10 text-brand-primary font-medium">
                          {u.plan}
                        </span>
                      </td>
                      <td className="py-2 text-brand-muted">
                        {dayjs(u.created_at).format('DD MMM, HH:mm')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Recent errors */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-brand-ink">Recent Errors</h2>
            <button
              onClick={() => void errorsQ.refetch()}
              className="p-1.5 rounded hover:bg-slate-100 text-brand-muted"
              title="Refresh"
            >
              <RefreshCw size={15} />
            </button>
          </div>
          {errorsQ.isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-12 bg-slate-100 rounded animate-pulse" />
              ))}
            </div>
          ) : errors.length === 0 ? (
            <p className="text-brand-muted text-sm flex items-center gap-2">
              <CheckCircle2 size={16} className="text-emerald-500" /> No recent errors.
            </p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {errors.slice(0, 8).map((e) => (
                <li key={e.id} className="py-2.5">
                  <div className="flex items-start gap-2">
                    <AlertCircle
                      size={14}
                      className={clsx(
                        'mt-0.5 shrink-0',
                        e.level === 'critical' ? 'text-red-600' : e.level === 'error' ? 'text-orange-500' : 'text-yellow-500',
                      )}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-xs text-brand-muted mb-0.5">
                        <span className="font-medium text-brand-ink">{e.service}</span>
                        <span>{dayjs(e.occurred_at).format('HH:mm:ss')}</span>
                      </div>
                      <p className="text-sm text-brand-ink truncate">{e.message}</p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Service status grid */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-brand-ink">Service Status</h2>
          <button
            onClick={() => void healthQ.refetch()}
            className="p-1.5 rounded hover:bg-slate-100 text-brand-muted"
            title="Refresh"
          >
            <RefreshCw size={15} />
          </button>
        </div>
        {healthQ.isLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {Array.from({ length: 9 }).map((_, i) => (
              <div key={i} className="h-20 bg-slate-100 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : services.length === 0 ? (
          <p className="text-brand-muted text-sm">No service data.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {services.map((svc) => (
              <div
                key={svc.name}
                className={clsx(
                  'p-3 rounded-lg border',
                  svc.status === 'healthy'
                    ? 'border-emerald-200 bg-emerald-50'
                    : svc.status === 'warning'
                    ? 'border-yellow-200 bg-yellow-50'
                    : 'border-red-200 bg-red-50',
                )}
              >
                <div className="flex items-center gap-1.5 mb-1.5">
                  <StatusDot status={svc.status} />
                  <span className="text-xs font-semibold text-brand-ink truncate">{svc.name}</span>
                </div>
                <div className="text-xs text-brand-muted space-y-0.5">
                  <div>CPU: {svc.cpuPct}%</div>
                  <div>Mem: {svc.memPct}%</div>
                  <div className="flex items-center gap-1">
                    <StatusIcon status={svc.status} />
                    <span>{svc.uptime}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
